import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { supabase } from '../lib/supabaseClient'

// Types
export type PartnerProfile = {
  id: string
  email: string
  full_name: string | undefined
  partner_id: string
  partner_role: 'ADMIN_PARTNER' | 'USER_PARTNER'
  role: 'PARTNER'
}

export type Partner = {
  id: string
  name: string
  address: string
  logo_url: string | null
  website_url: string | null
  contact_name: string
  contact_email: string
  contact_phone: string | null
  status: 'ACTIVE' | 'DISABLED'
  color_code: string
}

type PartnerAuthContextType = {
  partnerProfile: PartnerProfile | null
  partner: Partner | null
  loading: boolean
  isPartner: boolean
  isAdminPartner: boolean
}

const PartnerAuthContext = createContext<PartnerAuthContextType | undefined>(undefined)

/**
 * PartnerAuthProvider - Wrapper autour d'AuthContext pour gérer les données partenaires.
 * Réutilise Supabase Auth via AuthContext.
 */
export const PartnerAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { profile, loading: authLoading } = useAuth()
  const [partner, setPartner] = useState<Partner | null>(null)
  const [loading, setLoading] = useState(true)

  // Charger les donnees partenaire si le profil a un partner_id.
  useEffect(() => {
    const loadPartnerData = async () => {
      if (!profile || profile.role !== 'PARTNER' || !profile.partner_id) {
        setPartner(null)
        setLoading(false)
        return
      }

      try {
        const { data, error } = await supabase
          .from('partners')
          .select('*')
          .eq('id', profile.partner_id)
          .single()

        if (error) {
          console.error('Error loading partner:', error)
          setPartner(null)
        } else {
          setPartner(data)
        }
      } catch (error) {
        console.error('Error loading partner data:', error)
        setPartner(null)
      } finally {
        setLoading(false)
      }
    }

    if (!authLoading) {
      loadPartnerData()
    }
  }, [profile, authLoading])

  const partnerProfile = profile?.role === 'PARTNER' && profile.partner_id
    ? {
        id: profile.id,
        email: profile.email || '',
        full_name: profile.full_name,
        partner_id: profile.partner_id,
        partner_role: profile.partner_role as 'ADMIN_PARTNER' | 'USER_PARTNER',
        role: 'PARTNER' as const,
      }
    : null

  const isPartner = profile?.role === 'PARTNER' && !!profile.partner_id
  const isAdminPartner = isPartner && profile?.partner_role === 'ADMIN_PARTNER'

  return (
    <PartnerAuthContext.Provider
      value={{
        partnerProfile,
        partner,
        loading: authLoading || loading,
        isPartner,
        isAdminPartner,
      }}
    >
      {children}
    </PartnerAuthContext.Provider>
  )
}

export const usePartnerAuth = () => {
  const context = useContext(PartnerAuthContext)
  if (!context) {
    throw new Error('usePartnerAuth must be used within PartnerAuthProvider')
  }
  return context
}